import { useEffect, useRef } from 'react';
import {
  useAgentContainer,
  type AgentModuleConfig,
} from '@/context/agent-container.context';

interface RegisterOptions {
  /**
   * False keeps the hook mounted but registers nothing (and clears any prior
   * registration). Lets a layout-level fallback follow a feature flag.
   */
  enabled?: boolean;
}

/**
 * Shared body of the page and default registrations. Callbacks
 * (getModuleContext, renderToolResult) and headerIcon are stored via refs so
 * the renderer always uses the latest version without triggering
 * re-registration on every render.
 */
function useRegisterAgentModule(
  config: AgentModuleConfig,
  register: (config: AgentModuleConfig) => void,
  unregister: () => void,
  enabled: boolean,
): void {
  const getModuleContextRef = useRef(config.getModuleContext);
  getModuleContextRef.current = config.getModuleContext;

  const renderToolResultRef = useRef(config.renderToolResult);
  renderToolResultRef.current = config.renderToolResult;

  const headerIconRef = useRef(config.headerIcon);
  headerIconRef.current = config.headerIcon;

  const toolsRef = useRef(config.tools);
  toolsRef.current = config.tools;

  const onFocusNodesRef = useRef(config.onFocusNodes);
  onFocusNodesRef.current = config.onFocusNodes;

  // Content-keyed so an inline `suggestions` array does not re-register on
  // every render (same reasoning as useFrontendTool's schema key).
  const suggestionsKey = config.suggestions?.join('\u0000');

  useEffect(() => {
    if (!enabled) return;
    register({
      ...config,
      headerIcon: headerIconRef.current,
      getModuleContext: (...args) => getModuleContextRef.current?.(...args),
      renderToolResult: (...args) => renderToolResultRef.current?.(...args),
      tools: toolsRef.current,
      onFocusNodes: (...args) => onFocusNodesRef.current?.(...args),
    });

    return () => unregister();
    // Re-register when identity-stable values change. headerIcon and callbacks
    // are ref-wrapped so they never need to re-register. `tools` is re-read on
    // change so dynamically (un)registered tools — e.g. a step's
    // useDynamicStepState tool that appears when its config panel opens — reach
    // the agent. Callers must pass a memoized tools array (all do).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    config.moduleKey,
    config.pageKey,
    config.currentPage,
    config.scope,
    config.placeholder,
    config.inputNotice,
    config.headerTitle,
    config.tools,
    config.forcePanelEnabled,
    config.customRenderer,
    config.surfaceTitle,
    config.entity?.kind,
    config.entity?.id,
    config.entity?.label,
    suggestionsKey,
    register,
    unregister,
  ]);
}

/**
 * Register module context for the global chat agent renderer.
 *
 * Callbacks (getModuleContext, renderToolResult) and headerIcon are
 * stored via refs so the renderer always uses the latest version
 * without triggering re-registration on every render.
 */
export function useAgentModule(config: AgentModuleConfig): void {
  const { setModuleConfig, clearModuleConfig } = useAgentContainer();
  useRegisterAgentModule(config, setModuleConfig, clearModuleConfig, true);
}

/**
 * Register the layout-level fallback module the panel uses when the current
 * page registered nothing (the always-available Nami surface). Lives in its
 * own slot so a page's `useAgentModule` never clears it, and vice versa.
 */
export function useDefaultAgentModule(
  config: AgentModuleConfig,
  options: RegisterOptions = {},
): void {
  const { setDefaultModuleConfig, clearDefaultModuleConfig } =
    useAgentContainer();
  useRegisterAgentModule(
    config,
    setDefaultModuleConfig,
    clearDefaultModuleConfig,
    options.enabled ?? true,
  );
}
